import { cva, type VariantProps } from "class-variance-authority";
import type { ButtonHTMLAttributes } from "react";
import { cn } from "../../lib/cn";
import { toneVars } from "../../lib/tone";
import type { IconType } from "../../lib/types";
import { Spinner } from "../feedback/spinner";

/*
 * Primário chapado (cor sólida do tom, sem gradiente nem brilho); hover
 * escurece um degrau e o clique "afunda" 1px. Raio `sm` em todos os tamanhos.
 */
export const buttonVariants = cva(
  "inline-flex shrink-0 cursor-pointer items-center justify-center border border-transparent font-medium tracking-[-0.005em] whitespace-nowrap transition-[transform,background,border-color,color] duration-150 ease-out active:translate-y-px disabled:pointer-events-none disabled:opacity-50 aria-busy:cursor-progress aria-busy:opacity-70",
  {
    variants: {
      variant: {
        primary:
          "bg-(--tone) text-on-primary hover:bg-[color-mix(in_oklch,var(--tone)_88%,var(--fg))]",
        ghost: "border-border-hi bg-surface text-fg hover:bg-ov-2",
        outline: "border-(--tone) bg-transparent text-(--tone) hover:bg-(--tone-soft)",
        tonal:
          "bg-(--tone-soft) text-(--tone) hover:bg-[color-mix(in_oklch,var(--tone)_22%,transparent)]",
        text: "bg-transparent text-(--tone) hover:bg-(--tone-soft)",
      },
      tone: toneVars,
      size: {
        sm: "h-[34px] gap-1.5 rounded-sm px-3 text-[13px]",
        md: "h-10 gap-2 rounded-sm px-4 text-[14px]",
        lg: "h-12 gap-2 rounded-sm px-5 text-[15px]",
      },
      fullWidth: { true: "w-full" },
    },
    defaultVariants: { variant: "primary", tone: "ink", size: "md" },
  },
);

const ICON_SIZE = { sm: 15, md: 16, lg: 18 } as const;

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  loading?: boolean;
  iconLeft?: IconType;
  iconRight?: IconType;
  /**
   * Abaixo do breakpoint mostra só o ícone (botão quadrado); o rótulo segue
   * como nome acessível e tooltip. Exige `iconLeft`. Ex.: ações do header no mobile.
   */
  iconOnlyBelow?: "sm" | "md";
}

const ICON_ONLY = {
  sm: { button: "max-sm:aspect-square max-sm:gap-0 max-sm:px-0", label: "max-sm:sr-only" },
  md: { button: "max-md:aspect-square max-md:gap-0 max-md:px-0", label: "max-md:sr-only" },
} as const;

export function Button({
  className,
  variant,
  tone,
  size,
  fullWidth,
  loading = false,
  iconLeft: IconLeft,
  iconRight: IconRight,
  iconOnlyBelow,
  disabled,
  children,
  type = "button",
  title,
  ...props
}: ButtonProps) {
  const iconSize = ICON_SIZE[size ?? "md"];
  const iconOnly = iconOnlyBelow && IconLeft ? ICON_ONLY[iconOnlyBelow] : null;
  return (
    <button
      type={type}
      className={cn(
        buttonVariants({ variant, tone, size, fullWidth }),
        iconOnly?.button,
        className,
      )}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      title={title ?? (iconOnly && typeof children === "string" ? children : undefined)}
      {...props}
    >
      {loading ? <Spinner size="sm" /> : IconLeft && <IconLeft size={iconSize} weight="regular" />}
      {iconOnly ? <span className={iconOnly.label}>{children}</span> : children}
      {!loading && IconRight && <IconRight size={iconSize} weight="regular" />}
    </button>
  );
}
